import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { catchError, map, Observable, of, switchMap, filter } from 'rxjs';

import { RecaptchaResponse } from '@ninety/platform/models/_shared/recaptcha-response';
import { PersonService } from '@ninety/platform/services/services/person.service';
import { RecaptchaService } from '@ninety/platform/services/services/recaptcha-service';

import { FeatureFlagKeys } from '../../../app-entities/feature-flag/feature-flag-state.model';
import { selectFeatureFlag } from '../../../app-entities/feature-flag/feature-flag-state.selectors';
import { BaseEffects } from '../../../base.effects';
import { CONFIRM_SMS_DIALOG } from '../../../tokens/auth-dialog.tokens';
import { selectCurrentPersonId } from '../../current-person/current-person.selectors';
import { NotificationActions } from '../../notifications/notification.actions';
import { SpinnerActions } from '../../spinner/spinner-state.actions';
import { AuthActions } from '../auth.actions';
import { AuthStateSelectors } from '../auth.selectors';
import { AmplifyService } from '../services/amplify.service';
import { PhoneNumberWorkflows } from '../services/auth.types';
import { UserAccountActions } from '../user-account/user-account.actions';

import { PhoneNumberActions } from './change-phone-number.actions';

@Injectable()
export class ChangePhoneNumberEffects extends BaseEffects {
  cognitoService = inject(AmplifyService);
  store = inject(Store);
  recaptcha = inject(RecaptchaService);
  personService = inject(PersonService);
  http = inject(HttpClient);
  router = inject(Router);
  private readonly openConfirmSms = inject(CONFIRM_SMS_DIALOG);

  sendVerification$ = this.switchMapEffect({
    action: PhoneNumberActions.sendVerification,
    switchMapFn: ({ workflow, isResend }) =>
      this.cognitoService.sendPhoneNumberVerificationCode().pipe(
        map(() => PhoneNumberActions.verificationCodeSent({ workflow, isResend })),
        catchError((error: unknown) => of(PhoneNumberActions.verificationCodeSentError({ error, workflow })))
      ),
  });

  checkIsRobot$ = this.switchMapEffect({
    action: PhoneNumberActions.checkIsRobot,
    switchMapFn: ({ workflow }) =>
      this.recaptcha
        .executeAction('verifyPhone')
        .pipe(map(token => PhoneNumberActions.showRecaptcha({ token, workflow }))),
  });

  showRecaptcha$ = this.switchMapEffect({
    action: PhoneNumberActions.showRecaptcha,
    switchMapFn: ({ token, workflow }) =>
      this.recaptcha.verifyRecaptchaToken(token).pipe(
        map((response: RecaptchaResponse) => {
          if (response.success && response.score > 0.5) {
            return PhoneNumberActions.recaptchaSuccess({ workflow });
          }
          return NotificationActions.notify({
            message: 'There was a problem validating your request, please try again.',
          });
        }),
        catchError((error: unknown) => of(PhoneNumberActions.recaptchaError({ error, workflow })))
      ),
  });

  recaptchaSuccess$ = this.mapEffect({
    action: PhoneNumberActions.recaptchaSuccess,
    mapFn: ({ workflow }) => PhoneNumberActions.sendVerification({ workflow, isResend: false }),
  });

  updatePrimaryPhoneNumberWorkflow$ = this.mapEffect({
    action: PhoneNumberActions.startUpdateWorkflow,
    mapFn: ({ phoneNumber, workflow }) => PhoneNumberActions.checkBlacklist({ phoneNumber, workflow }),
  });

  checkPhoneNumberBlacklist$ = this.mapWithConcatEffect({
    action: PhoneNumberActions.checkBlacklist,
    concatLatestFromFn: () => this.store.select(selectFeatureFlag(FeatureFlagKeys.mfaDenyList)),
    mapFn: ({ phoneNumber, workflow }, denyList) =>
      denyList.some(prefix => phoneNumber.startsWith(prefix))
        ? PhoneNumberActions.isBlacklisted({ phoneNumber, workflow })
        : PhoneNumberActions.updatePerson({ phoneNumber, workflow }),
  });

  phoneNumberBlacklisted$ = this.mapEffect({
    action: PhoneNumberActions.isBlacklisted,
    mapFn: () =>
      NotificationActions.showError({
        message: 'Unable to update your phone number. Please contact support.',
        title: 'Phone Number Update Failed',
      }),
  });

  updatePersonPhoneNumber$ = this.switchMapWithConcatEffect({
    action: PhoneNumberActions.updatePerson,
    concatLatestFromFn: () => this.store.select(selectCurrentPersonId),
    switchMapFn: ({ phoneNumber, workflow }, personId) =>
      this.personService.updatePerson({ primaryPhoneNumber: phoneNumber }, personId).pipe(
        map(() => PhoneNumberActions.updatePersonSuccess({ phoneNumber, workflow })),
        catchError((error: unknown) => of(PhoneNumberActions.updatePersonError({ error, workflow })))
      ),
  });

  updatePersonPhoneNumber_success$ = this.mapEffect({
    action: PhoneNumberActions.updatePersonSuccess,
    mapFn: () => NotificationActions.showSnackbar({ message: 'Profile information updated.' }),
  });

  updatePersonPhoneNumberSuccess_updateCognito$ = this.switchMapEffect({
    action: PhoneNumberActions.updatePersonSuccess,
    switchMapFn: ({ phoneNumber, workflow }) =>
      this.cognitoService.updatePhoneNumber(phoneNumber).pipe(
        map(() => PhoneNumberActions.updateCognitoSuccess({ phoneNumber, workflow })),
        catchError((error: unknown) => of(PhoneNumberActions.updateCognitoError({ error, workflow })))
      ),
  });

  // Prevent user from updating to unverified SMS number and keeping MFA on, causing lockout on next login.
  updateCognitoPhoneNumberSuccess_disableMFA$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PhoneNumberActions.updateCognitoSuccess),
      concatLatestFrom(() => [
        this.store.select(AuthStateSelectors.isMfaEnabled),
        this.store.select(AuthStateSelectors.selectHasNonSmsMfaEnabled),
      ]),
      filter(([, mfaEnabled]) => mfaEnabled),
      switchMap(([, , hasRemainingMfaEnabled]) =>
        this.cognitoService.disableSms().pipe(
          map(() => UserAccountActions.disableMFASuccess({ hasRemainingMfaEnabled })),
          catchError((error: unknown) => of(UserAccountActions.disableMFAFailure({ error })))
        )
      )
    )
  );

  updateCognitoSuccess_confirmCognito$ = this.switchMapEffect({
    action: PhoneNumberActions.updateCognitoSuccess,
    switchMapFn: ({ workflow }) =>
      this.cognitoService.sendPhoneNumberVerificationCode().pipe(
        map(() => PhoneNumberActions.openConfirmationDialog({ workflow })),
        catchError((error: unknown) => of(PhoneNumberActions.verificationCodeSentError({ error, workflow })))
      ),
  });

  openConfirmDialog$ = this.switchMapEffect({
    action: PhoneNumberActions.openConfirmationDialog,
    switchMapFn: ({ workflow }) => this.openConfirmationDialog(workflow),
  });

  submitConfirmationCode$ = this.switchMapEffect({
    action: PhoneNumberActions.submitConfirmationCode,
    switchMapFn: ({ code, workflow }) =>
      this.cognitoService.confirmPhoneNumberVerificationCode(code).pipe(
        map(() => PhoneNumberActions.confirmationCodeConfirmed({ workflow })),
        catchError((error: unknown) => of(PhoneNumberActions.confirmationCodeError({ error, workflow })))
      ),
  });

  confirmationCodeConfirmed$ = this.mapEffect({
    action: PhoneNumberActions.confirmationCodeConfirmed,
    mapFn: () => AuthActions.getCognitoUserAttributes(),
  });

  confirmationCodeError$ = this.mapEffect({
    action: PhoneNumberActions.confirmationCodeError,
    mapFn: ({ workflow }) => PhoneNumberActions.openConfirmationDialog({ workflow }),
  });

  phoneVerificationCodeSent$ = this.mapEffect({
    action: PhoneNumberActions.verificationCodeSent,
    filterFn: ({ isResend }) => isResend === false,
    mapFn: ({ workflow }) => PhoneNumberActions.openConfirmationDialog({ workflow }),
  });

  refreshCognitoAttributes = createEffect(() =>
    this.actions$.pipe(
      ofType(PhoneNumberActions.updateCognitoSuccess, PhoneNumberActions.confirmationCodeConfirmed),
      map(() => AuthActions.getCognitoUserAttributes())
    )
  );

  startSpinner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PhoneNumberActions.startUpdateWorkflow,
        PhoneNumberActions.checkIsRobot,
        PhoneNumberActions.submitConfirmationCode
      ),
      map(({ type: source }) => SpinnerActions.startPrimary({ source }))
    )
  );

  stopSpinner_confirmationCodeSuccess$ = this.mapEffect({
    action: PhoneNumberActions.confirmationCodeConfirmed,
    filterFn: ({ workflow }) => workflow !== 'LOGIN',
    mapFn: ({ type: source }) => SpinnerActions.stopPrimary({ source }),
  });

  stopSpinner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PhoneNumberActions.isBlacklisted,
        PhoneNumberActions.updatePersonError,
        PhoneNumberActions.updateCognitoError,
        PhoneNumberActions.recaptchaError
      ),
      map(({ type: source }) => SpinnerActions.stopPrimary({ source }))
    )
  );

  private openConfirmationDialog(workflow: PhoneNumberWorkflows): Observable<Action> {
    return this.openConfirmSms({
      data: { destination: '', resendAction: PhoneNumberActions.sendVerification({ workflow, isResend: true }) },
    })
      .afterClosed()
      .pipe(
        map(({ code, cancelled }) =>
          cancelled
            ? PhoneNumberActions.cancelConfirmation()
            : PhoneNumberActions.submitConfirmationCode({ code, workflow })
        )
      );
  }
}
